import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { UserInfoService } from '@telecom/shared/domain/permission';
import { HeaderServiceProxy } from '@telecom/shared/services/header-service/header-service-proxy';
import { getIsOversea } from '@telecom/shared/utils/environment';
import {
    EurekaAllHistoryService,
    type EurekaAllHistoryService as EurekaAllHistoryServiceType,
} from '@/domains/task/services/eureka-all-history-service';
import {
    OnboardingBrowserRuntime,
    type OnboardingBrowserRuntime as OnboardingBrowserRuntimeType,
} from '../onboarding-browser-runtime';
import {
    OnboardingStateService,
    type OnboardingStateService as OnboardingStateServiceType,
} from '../onboarding-state-service';
import type { OnboardingEligibilityService } from '../onboarding-eligibility-service';

const ELIGIBLE_SNAPSHOT = 'eligible';
const INELIGIBLE_SNAPSHOT = 'ineligible';
const ENTRY_SEEN_SNAPSHOT = 'seen';
const ONBOARDING_START_DELAY_MS = 1000;

/** 分别处理启动页和报告页的账号资格，并复用统一的公共受众判断。 */
@injectable()
export class OnboardingEligibilityServiceImpl implements OnboardingEligibilityService {
    /** 同一账号正在执行的启动页资格判断。 */
    private readonly _pendingEntryEvaluations = new Map<string, Promise<boolean>>();

    /** 同一账号正在执行的报告页资格判断。 */
    private readonly _pendingReportEvaluations = new Map<string, Promise<boolean>>();

    // eslint-disable-next-line max-params
    constructor(
        /** 提供当前账号的个人或企业租户判断。 */
        @inject(UserInfoService)
        private readonly _userInfoService: UserInfoService,
        /** 提供当前账号的同步登录状态和账号 ID。 */
        @inject(HeaderServiceProxy)
        private readonly _headerServiceProxy: HeaderServiceProxy,
        /** 提供当前账号的 Eureka 历史任务，用于启动页资格判断。 */
        @inject(EurekaAllHistoryService)
        private readonly _eurekaAllHistoryService: EurekaAllHistoryServiceType,
        /** 提供计时器和异常记录能力。 */
        @inject(OnboardingBrowserRuntime)
        private readonly _runtime: OnboardingBrowserRuntimeType,
        /** 提供账号级 onboarding 后端状态。 */
        @inject(OnboardingStateService)
        private readonly _onboardingStateService: OnboardingStateServiceType,
    ) {}

    /** 同步复用登录、账号 ID、海外区域、个人租户和免费用户五项公共资格判断。 */
    isAudienceEligible(): boolean {
        const account = this._headerServiceProxy.headerService.account;
        return Boolean(
            account?.isLogin
            && this._getAccountId()
            && getIsOversea()
            && this._userInfoService.isPersonalTenant()
            && this._userInfoService.isFreeUser(),
        );
    }

    /** 判断启动页资格并按账号固化入口历史快照。 */
    async isEligible(): Promise<boolean> {
        if (!this.isAudienceEligible()) return false;

        const accountId = this._getAccountId();
        const pendingEvaluation = this._pendingEntryEvaluations.get(accountId);
        if (pendingEvaluation) return pendingEvaluation;

        const evaluation = this._evaluateEntryEligibility(accountId)
            .then(isEligible => this._waitForStartDelay(isEligible));
        this._pendingEntryEvaluations.set(accountId, evaluation);
        try {
            return await evaluation;
        }
        finally {
            if (this._pendingEntryEvaluations.get(accountId) === evaluation) {
                this._pendingEntryEvaluations.delete(accountId);
            }
        }
    }

    /** 根据账号级奖励弹窗展示状态判断报告页资格。 */
    async isReportEligible(): Promise<boolean> {
        if (!this.isAudienceEligible()) return false;

        const accountId = this._getAccountId();
        const pendingEvaluation = this._pendingReportEvaluations.get(accountId);
        if (pendingEvaluation) return pendingEvaluation;

        const evaluation = this._evaluateReportEligibility(accountId);
        this._pendingReportEvaluations.set(accountId, evaluation);
        try {
            return await evaluation;
        }
        finally {
            if (this._pendingReportEvaluations.get(accountId) === evaluation) {
                this._pendingReportEvaluations.delete(accountId);
            }
        }
    }

    /** 将全局入口快照从可展示更新为已展示。 */
    markEntryOnboardingSeen(): void {
        const accountId = this._getAccountId();
        if (!accountId) return;

        void this._onboardingStateService.getState(accountId).then((state) => {
            if (state.entry_status !== ELIGIBLE_SNAPSHOT && state.entry_status !== ENTRY_SEEN_SNAPSHOT) return;
            return this._onboardingStateService.updateState(
                accountId,
                { entry_status: ENTRY_SEEN_SNAPSHOT },
                'optimistic',
            );
        }).catch((error) => {
            this._runtime.warn('Failed to mark entry onboarding as seen', error);
        });
    }

    /** 读取当前登录账号的稳定 ID。 */
    private _getAccountId(): string {
        return String(this._headerServiceProxy.headerService.account?._info.account_id ?? '');
    }

    /** 仅在资格通过时等待统一的 onboarding 启动延时。 */
    private async _waitForStartDelay(isEligible: boolean): Promise<boolean> {
        if (!isEligible) return false;

        try {
            await new Promise<void>((resolve) => {
                this._runtime.setTimeout(resolve, ONBOARDING_START_DELAY_MS);
            });
            return true;
        }
        catch (error) {
            this._runtime.warn('Failed to wait for onboarding start delay', error);
            return false;
        }
    }

    /** 启动页先排除已完成状态，再以真实历史覆盖可能过期的 eligible 快照。 */
    private async _evaluateEntryEligibility(accountId: string): Promise<boolean> {
        try {
            const state = await this._onboardingStateService.getState(accountId);
            const snapshot = state.entry_status;
            if (snapshot === INELIGIBLE_SNAPSHOT || snapshot === ENTRY_SEEN_SNAPSHOT) return false;

            const hasHistory = await this._eurekaAllHistoryService.hasHistory();
            if (hasHistory) {
                await this._onboardingStateService.updateState(
                    accountId,
                    { entry_status: INELIGIBLE_SNAPSHOT },
                    'required',
                );
                return false;
            }
            if (snapshot === ELIGIBLE_SNAPSHOT) return true;

            await this._onboardingStateService.updateState(
                accountId,
                { entry_status: ELIGIBLE_SNAPSHOT },
                'required',
            );
            return true;
        }
        catch (error) {
            this._runtime.warn('Failed to resolve onboarding eligibility', error);
            return false;
        }
    }

    /** 奖励弹窗未展示过时允许报告页进入后续展示流程。 */
    private async _evaluateReportEligibility(accountId: string): Promise<boolean> {
        try {
            const state = await this._onboardingStateService.getState(accountId);
            return state.first_task_reward_prompt_seen !== true;
        }
        catch (error) {
            this._runtime.warn('Failed to resolve report onboarding eligibility', error);
            return false;
        }
    }
}
